import React, { useEffect, useMemo, useState } from "react";
import { PageHeader, StatCard } from "../components/PageHeader";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Plus, Check, X, Trash, ClipboardText } from "@phosphor-icons/react";

const STATUS_STYLES = {
  pending: { bg: "#FEF3C7", fg: "#92400E", label: "Pending" },
  approved: { bg: "#D1FAE5", fg: "#065F46", label: "Approved" },
  rejected: { bg: "#FEE2E2", fg: "#991B1B", label: "Rejected" },
};

export default function Leaves() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [filterStatus, setFilterStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    from_date: new Date().toISOString().slice(0, 10),
    to_date: new Date().toISOString().slice(0, 10),
    leave_type: "", reason: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const [{ data: leaves }, { data: settings }] = await Promise.all([
      api.get("/leaves", { params: filterStatus ? { status: filterStatus } : {} }),
      api.get("/settings"),
    ]);
    setItems(leaves);
    setTypes((settings.leave_type || []).map((t) => t.value));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filterStatus]);

  const days = useMemo(() => {
    if (!form.from_date || !form.to_date) return 0;
    const a = new Date(form.from_date);
    const b = new Date(form.to_date);
    if (b < a) return 0;
    return Math.round((b - a) / 86400000) + 1;
  }, [form.from_date, form.to_date]);

  const submit = async (e) => {
    e.preventDefault();
    if (new Date(form.to_date) < new Date(form.from_date)) {
      toast.error("End date cannot be before start date");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leaves", form);
      toast.success("Leave request submitted");
      setShowForm(false);
      setForm({ from_date: new Date().toISOString().slice(0, 10),
                to_date: new Date().toISOString().slice(0, 10), leave_type: "", reason: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const approve = async (l) => {
    try {
      await api.post(`/leaves/${l.id}/approve`, { review_notes: "" });
      toast.success("Leave approved");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const reject = async (l) => {
    const notes = window.prompt("Reason for rejecting (optional):", "");
    if (notes === null) return;
    try {
      await api.post(`/leaves/${l.id}/reject`, { review_notes: notes });
      toast.success("Leave rejected");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const del = async (l) => {
    if (!window.confirm("Delete this leave request?")) return;
    await api.delete(`/leaves/${l.id}`);
    toast.success("Deleted"); load();
  };

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0 };
    for (const i of items) c[i.status] = (c[i.status] || 0) + 1;
    return c;
  }, [items]);

  return (
    <div>
      <PageHeader
        eyebrow="Time Off"
        title="Leave Requests"
        subtitle={isAdmin ? "Approve or reject pending leave requests with one click." : "Request time off and track approvals."}
        actions={
          !isAdmin && (
            <button onClick={() => setShowForm(true)} data-testid="new-leave-btn"
                    className="brand-btn px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
              <Plus size={16} weight="bold" /> Request Leave
            </button>
          )
        }
      />

      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard label={isAdmin ? "Pending" : "My Pending"} value={counts.pending} testId="stat-leave-pending" />
          <StatCard label={isAdmin ? "Approved" : "My Approved"} value={counts.approved} testId="stat-leave-approved" />
          <StatCard label={isAdmin ? "Rejected" : "My Rejected"} value={counts.rejected} testId="stat-leave-rejected" />
        </div>

        {/* Filter */}
        <div className="flex items-center gap-3 flex-wrap">
          <label className="label-caps">Filter Status</label>
          <div className="flex border border-[#E5E7EB]">
            {[{ v: "", l: "All" }, { v: "pending", l: "Pending" }, { v: "approved", l: "Approved" }, { v: "rejected", l: "Rejected" }].map((o) => (
              <button key={o.v} onClick={() => setFilterStatus(o.v)}
                      data-testid={`filter-${o.v || "all"}`}
                      className={`px-3 py-2 text-sm border-r border-[#E5E7EB] last:border-r-0 transition-colors duration-200 ${filterStatus === o.v ? "bg-[#106109] text-white" : "hover:bg-[#F9FAFB]"}`}>
                {o.l}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div className="bg-white border border-[#E5E7EB] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr className="text-left">
                {isAdmin
                  ? ["Employee", "Type", "From → To", "Days", "Reason", "Status", "Actions"].map((h) => (
                      <th key={h} className="px-4 py-3 label-caps">{h}</th>
                    ))
                  : ["Type", "From → To", "Days", "Reason", "Status", "Actions"].map((h) => (
                      <th key={h} className="px-4 py-3 label-caps">{h}</th>
                    ))}
              </tr>
            </thead>
            <tbody>
              {items.map((l) => {
                const s = STATUS_STYLES[l.status] || STATUS_STYLES.pending;
                return (
                  <tr key={l.id} className="border-b border-[#F3F4F6]" data-testid={`leave-row-${l.id}`}>
                    {isAdmin && (
                      <td className="px-4 py-3">
                        <div className="font-medium">{l.employee_name}</div>
                        <div className="text-xs text-[#6B7280]">{l.employee_code} · {l.department}</div>
                      </td>
                    )}
                    <td className="px-4 py-3">{l.leave_type}</td>
                    <td className="px-4 py-3 font-mono text-xs">{l.from_date} → {l.to_date}</td>
                    <td className="px-4 py-3">{l.days}</td>
                    <td className="px-4 py-3 text-[#6B7280] max-w-xs truncate" title={l.reason}>{l.reason}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-1 text-xs font-semibold" style={{ background: s.bg, color: s.fg }} data-testid={`status-${l.id}`}>
                        {s.label}
                      </span>
                      {l.reviewed_by && (
                        <div className="text-[10px] text-[#6B7280] mt-1">by {l.reviewed_by}</div>
                      )}
                      {l.review_notes && (
                        <div className="text-[10px] text-[#6B7280] mt-0.5 italic">"{l.review_notes}"</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {isAdmin && l.status === "pending" && (
                          <>
                            <button onClick={() => approve(l)} data-testid={`approve-${l.id}`}
                                    className="border border-[#10B981] text-[#10B981] p-1.5 hover:bg-[#ECFDF5]" title="Approve">
                              <Check size={14} weight="bold" />
                            </button>
                            <button onClick={() => reject(l)} data-testid={`reject-${l.id}`}
                                    className="border border-[#DC2626] text-[#DC2626] p-1.5 hover:bg-[#FEF2F2]" title="Reject">
                              <X size={14} weight="bold" />
                            </button>
                          </>
                        )}
                        {(isAdmin || (l.status === "pending" && l.employee_id === user?.id)) && (
                          <button onClick={() => del(l)} data-testid={`delete-leave-${l.id}`}
                                  className="border border-[#E5E7EB] p-1.5 hover:bg-[#FEF2F2] text-[#DC2626]" title="Delete">
                            <Trash size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr>
                  <td colSpan={isAdmin ? 7 : 6} className="px-4 py-16 text-center text-[#9CA3AF]">
                    <ClipboardText size={32} weight="thin" className="mx-auto mb-2 opacity-60" />
                    No leave requests {filterStatus ? `with status "${filterStatus}"` : "yet"}.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="leave-form-modal">
          <div className="bg-white w-full max-w-lg border border-[#E5E7EB]">
            <div className="border-b border-[#E5E7EB] px-6 py-4">
              <div className="label-caps">Request</div>
              <h3 className="font-display font-bold text-xl">New Leave Request</h3>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-caps block mb-1.5">From</label>
                  <input type="date" required value={form.from_date}
                         onChange={(e) => setForm({ ...form, from_date: e.target.value })}
                         className="w-full border border-[#E5E7EB] px-3 py-2 text-sm"
                         data-testid="leave-form-from" />
                </div>
                <div>
                  <label className="label-caps block mb-1.5">To</label>
                  <input type="date" required value={form.to_date}
                         onChange={(e) => setForm({ ...form, to_date: e.target.value })}
                         className="w-full border border-[#E5E7EB] px-3 py-2 text-sm"
                         data-testid="leave-form-to" />
                </div>
              </div>
              <div className="text-xs text-[#6B7280]">
                Duration: <b className="text-[#111827]">{days} {days === 1 ? "day" : "days"}</b>
              </div>
              <div>
                <label className="label-caps block mb-1.5">Leave Type</label>
                <select required value={form.leave_type}
                        onChange={(e) => setForm({ ...form, leave_type: e.target.value })}
                        className="w-full border border-[#E5E7EB] px-3 py-2 text-sm"
                        data-testid="leave-form-type">
                  <option value="">Select…</option>
                  {types.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="label-caps block mb-1.5">Reason</label>
                <textarea required rows={4} value={form.reason}
                          onChange={(e) => setForm({ ...form, reason: e.target.value })}
                          className="w-full border border-[#E5E7EB] px-3 py-2 text-sm"
                          data-testid="leave-form-reason" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2.5 text-sm border border-[#E5E7EB]">Cancel</button>
                <button type="submit" disabled={submitting} data-testid="leave-form-submit"
                        className="brand-btn flex-1 py-2.5 text-sm font-semibold disabled:opacity-60">
                  {submitting ? "Submitting…" : "Submit Request"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
